import React, { useEffect, useState } from 'react';
import { lineDiff } from '../utils/lineDiff';
import './PpDryRunModal.css';

// Dry Run result viewer. Shows the exact OHIP PUT bodies a real run would send, plus the before → after
// diff — nothing is sent to the PMS. Reuses the .pp-modal-* shell and .pp-diff-* classes from
// PaymentPorterPage.css so it looks like the rest of Payment Porter.

const STATUS_STYLE = {
    DRY_RUN: { label: 'Would run',  cls: 'pdr-badge-ok' },
    SKIPPED: { label: 'Would skip', cls: 'pdr-badge-skip' },
    ERROR:   { label: 'Would fail', cls: 'pdr-badge-error' },
};

const Json = ({ value }) => <pre className="pdr-pre">{JSON.stringify(value, null, 2)}</pre>;

// Line diff of two JSON documents. Unchanged lines are dimmed context so the +/- lines stand out — the
// point of the Dry Run is "what changes", not "here is a payload, compare it yourself".
const JsonDiff = ({ from, to }) => (
    <pre className="pdr-pre pdr-diff">
        {lineDiff(JSON.stringify(from ?? {}, null, 2), JSON.stringify(to ?? {}, null, 2)).map((l, i) => (
            <div key={i} className={l.t === 'add' ? 'pp-diff-add' : l.t === 'del' ? 'pp-diff-del' : 'pp-diff-same'}>
                <span className="pp-diff-gutter">{l.t === 'add' ? '+' : l.t === 'del' ? '-' : ' '}</span>{l.s}
            </div>
        ))}
    </pre>
);

// One PUT: diff by default (baseline → body), raw body available behind a toggle for copy/paste into
// Postman. Falls back to raw when the server sent no baseline.
const PutBlock = ({ put, copied, onCopy }) => {
    const [raw, setRaw] = useState(false);
    const canDiff = !!put.baseline;
    return (
        <div className="pdr-put">
            <div className="pdr-put-head">
                <span className="pdr-put-phase">PUT {put.phase}</span>
                <span className="pdr-put-label">{put.label}</span>
                {canDiff && (
                    <button className="pdr-toggle" onClick={() => setRaw(v => !v)}>
                        {raw ? 'Show diff' : 'Show full payload'}
                    </button>
                )}
                <button className="pdr-copy" onClick={() => onCopy(`put${put.phase}`, put.body)}>
                    {copied === `put${put.phase}` ? 'Copied' : copied === 'failed' ? 'Copy failed' : 'Copy JSON'}
                </button>
            </div>
            {put.note && <div className="pdr-note">{put.note}</div>}
            {put.predicted && (
                // The live run rebuilds phase 2 from what OHIP returns after phase 1, so this body is a
                // prediction. Said plainly rather than shown as fact.
                <div className="pdr-predicted">
                    Predicted — the real run rebuilds this from OHIP's response to PUT 1, so it can differ if OHIP alters the payment methods.
                </div>
            )}
            {canDiff && !raw && (
                <div className="pdr-diff-legend">
                    <span className="pdr-legend-del">− current</span>
                    <span className="pdr-legend-add">+ would be sent</span>
                </div>
            )}
            {canDiff && !raw
                ? <JsonDiff from={put.baseline} to={put.body} />
                : <Json value={put.body} />}
        </div>
    );
};

const PpDryRunModal = ({ reservationLabel, loading, error, result, onClose }) => {
    const [copied, setCopied] = useState(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const copy = (label, value) => {
        // Clipboard write can reject (permissions, non-secure context); a debugging aid should not throw.
        navigator.clipboard?.writeText(JSON.stringify(value, null, 2))
            .then(() => { setCopied(label); setTimeout(() => setCopied(null), 1500); })
            .catch(() => setCopied('failed'));
    };

    const status = result?.status && STATUS_STYLE[result.status];
    const puts   = result?.puts || [];

    return (
        <div className="pp-modal-backdrop" onClick={onClose}>
            <div className="pp-modal pdr-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className="pp-modal-header">
                    <span>Dry Run — reservation {reservationLabel} <span className="pdr-nosend">nothing sent to OHIP</span></span>
                    <button className="pp-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="pdr-body">
                    {loading && <div className="pdr-status">Fetching the reservation from OHIP and planning the update…</div>}
                    {error && <div className="pdr-error">{error}</div>}

                    {!loading && !error && result && (
                        <>
                            <div className="pdr-summary">
                                {status && <span className={`pdr-badge ${status.cls}`}>{status.label}</span>}
                                {result.ruleName && (
                                    <span className="pdr-summary-item">
                                        Rule <strong>{result.ruleName}</strong>{result.ruleId ? ` (#${result.ruleId})` : ''}
                                    </span>
                                )}
                                {result.plan?.targetFolioWindow != null && (
                                    <span className="pdr-summary-item">Target window <strong>{result.plan.targetFolioWindow}</strong></span>
                                )}
                                {result.plan?.cardId && (
                                    <span className="pdr-summary-item">
                                        Card <strong>{result.plan.cardMasked || result.plan.cardId}</strong>
                                        {result.plan.paymentMethodCode ? ` · ${result.plan.paymentMethodCode}` : ''}
                                    </span>
                                )}
                                {result.plan?.authorizationRule && (
                                    <span className="pdr-summary-item">
                                        Auth rule <strong>{result.plan.authorizationRule.code}</strong>
                                        {/* OHIP money object: { amount: { amount, currencyCode } } */}
                                        {result.plan.authorizationRule.amount?.amount != null
                                            ? ` · amount ${result.plan.authorizationRule.amount.amount}${result.plan.authorizationRule.amount.currencyCode ? ` ${result.plan.authorizationRule.amount.currencyCode}` : ''}`
                                            : ' · no amount'}
                                    </span>
                                )}
                                {result.plan?.routingInstructions > 0 && (
                                    <span className="pdr-summary-item">{result.plan.routingInstructions} routing instruction(s)</span>
                                )}
                            </div>

                            {result.error && <div className="pdr-error">{result.error}</div>}

                            {/* A higher-priority rule owns this reservation. Called out ABOVE the generic skip
                                line and in its own style, because "your rule matched but another one wins" is a
                                different answer from "nothing matched" and the tester must not confuse them. */}
                            {result.preemptedBy && (
                                <div className="pdr-preempt">
                                    <strong>Another rule matches first: {result.preemptedBy.name}</strong>
                                    {result.preemptedBy.id ? ` (#${result.preemptedBy.id})` : ''}
                                    <div className="pdr-preempt-detail">
                                        {result.testRule?.qualifies
                                            ? `"${result.testRule.name}" also matches, but ${result.preemptedBy.name} has higher priority, so it would process this reservation.`
                                            : `"${result.testRule?.name || 'The rule under test'}" does not qualify on the full reservation detail.`}
                                    </div>
                                </div>
                            )}

                            {result.skipReason && !result.preemptedBy && <div className="pdr-skip">{result.skipReason}</div>}

                            {result.matchedCriteria?.length > 0 && (
                                <details className="pdr-section">
                                    <summary>Matched criteria ({result.matchedCriteria.length})</summary>
                                    <ul className="pdr-list">
                                        {result.matchedCriteria.map((c, i) => <li key={i}>{c}</li>)}
                                    </ul>
                                </details>
                            )}

                            {result.qualificationDetail?.length > 0 && (
                                <details className="pdr-section">
                                    <summary>Why no rule qualified</summary>
                                    <Json value={result.qualificationDetail} />
                                </details>
                            )}

                            {puts.length > 0 && (
                                <div className="pdr-section-block">
                                    <div className="pdr-heading">
                                        {puts.length === 1 ? 'The PUT that would be sent' : `The ${puts.length} PUTs that would be sent, in order`}
                                    </div>
                                    {puts.map(p => (
                                        <PutBlock key={p.phase} put={p} copied={copied} onCopy={copy} />
                                    ))}
                                </div>
                            )}

                            {/* Net effect across both PUTs. Collapsed: the per-PUT diffs above are the
                                detail, this is the summary for "what does the reservation end up like". */}
                            {result.before && result.after && (
                                <details className="pdr-section">
                                    <summary>Net effect — reservation before → after (both PUTs combined)</summary>
                                    <JsonDiff from={result.before} to={result.after} />
                                </details>
                            )}

                            {result.before && !result.after && (
                                <details className="pdr-section">
                                    <summary>Reservation as it stands (no change planned)</summary>
                                    <Json value={result.before} />
                                </details>
                            )}

                            {result.comment && (
                                <details className="pdr-section">
                                    <summary>Reservation comment that would be written</summary>
                                    <div className="pdr-comment">{result.comment}</div>
                                </details>
                            )}
                        </>
                    )}
                </div>

                <div className="pdr-footer">
                    <button className="pp-run-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default PpDryRunModal;
